import { LogOut, Lock, Loader2, AlertCircle } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useLockStore } from "../store/lockStore";
import { useLockActions } from "../hooks/useLockActions";
import { useAuth } from "../auth/AuthProvider";

interface RemovedScreenProps {
  formId: string;
  /**
   * Refetch form data when Request Access falls back to a direct acquire
   * (lock TTL'd between the user landing on this screen and clicking the
   * button). Forwarded to useLockActions.
   */
  onLockGranted?: () => Promise<void>;
}

const HEADING_ID = "removed-screen-heading";

// How long to hold the "transition in progress" state before exposing the
// action buttons. The new holder's useLockManager.startGrantedTransition
// waits ACCESS_GRANTED_DISPLAY_MS (2000ms) before calling acquireLock; that
// call typically resolves in <200ms. 2500ms here gives a buffer so by the
// time this screen's buttons enable, the lock is reliably claimed and a
// click on Request Access can't slip into the unclaimed window. This is UX
// polish on top of the server-side conditional HDEL in AcquireLockScript —
// even if the gate is bypassed, the data layer no longer deadlocks.
const TRANSITION_DISPLAY_MS = 2500;

/**
 * Shown when the caller's lock was transferred to another user (either via
 * Give Access or auto-grant after the 30s timer expired). The form is no
 * longer accessible; the user can sign out or re-request access.
 */
export function RemovedScreen({ formId, onLockGranted }: RemovedScreenProps) {
  const lockedBy = useLockStore((s) => s.lockedBy);
  const { requestAccess, reclaim } = useLockActions(formId, { onLockGranted });
  const { logout, user } = useAuth();
  const [transitionComplete, setTransitionComplete] = useState(false);
  const requestButtonRef = useRef<HTMLButtonElement>(null);

  // Same-user reclaim: the lock was taken by the same user under a different
  // session (they opened the form on phone while desktop was still active, or
  // a second tab in the same browser). The new device acquired immediately via
  // Path 4 of AcquireLockScript; this tab's heartbeat detected the loss and
  // sent us here. Different copy + a Take Over button that calls reclaim()
  // instead of requestAccess() — requesting access from yourself would file a
  // Pending request to your own session, which is nonsensical UX.
  const myEmail = user?.profile.email as string | undefined;
  const isSameUserHolder = !!myEmail && !!lockedBy &&
    lockedBy.toLowerCase() === myEmail.toLowerCase();

  // Session-expired variant (ADO-3515): the server evicted us with no new
  // holder, i.e. our Redis lock's 90s TTL ran out while the tab was throttled/
  // frozen/offline. HeartbeatScript returns lockedBy='' in that branch, which
  // hydrates as null in the store. Distinct copy + an "Access Form" button
  // that goes straight to acquireLock via reclaim() — there is no other holder
  // to request access from, so the Request-Access flow doesn't apply.
  const isSessionExpired = !isSameUserHolder && !lockedBy;

  // Mirror LockScreen's "another user is requesting" logic: if a third party
  // (e.g. user C) filed a Pending request while we're on RemovedScreen after
  // being removed by user B, server-side RequestAccessScript blocks a second
  // Pending — our Request Access click would 200 with submitted=false, looking
  // like a no-op. Surface the in-progress request and disable the button until
  // it resolves so the click doesn't appear to do nothing.
  // Self-match guard: requestInProgress can transiently be our own email
  // around our own Request Access click (broadcast lap of the push) — never
  // disable the button against ourselves.
  const requestInProgress = useLockStore((s) => s.requestInProgress);
  const otherUserRequesting = !!requestInProgress &&
    (!myEmail || requestInProgress.toLowerCase() !== myEmail.toLowerCase());
  // Take Over Here in the same-user variant is a direct acquire that bypasses
  // the request flow entirely — no reason to gate it on another user's request.
  const buttonDisabled = !isSameUserHolder && otherUserRequesting;

  useEffect(() => {
    // The 2.5s transition gate exists so the button doesn't enable during the
    // new holder's takeover window. For the session-expired variant there is
    // no new holder — the user has just returned from a break — so skip the
    // gate and expose Access Form / Sign Out immediately.
    if (isSessionExpired) return;
    const id = window.setTimeout(() => setTransitionComplete(true), TRANSITION_DISPLAY_MS);
    return () => window.clearTimeout(id);
  }, [isSessionExpired]);

  // Move keyboard focus to the primary button once it's on screen. For the
  // session-expired variant the button renders immediately; otherwise we wait
  // for the takeover transition gate to release.
  useEffect(() => {
    if (isSessionExpired || transitionComplete) requestButtonRef.current?.focus();
  }, [isSessionExpired, transitionComplete]);

  if (isSessionExpired) {
    return (
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={HEADING_ID}
        className="min-h-screen flex items-center justify-center bg-gray-50 px-6"
      >
        <div className="max-w-md w-full bg-white rounded-xl border border-gray-200 p-8 shadow-sm text-center">
          <div className="w-14 h-14 mx-auto rounded-full bg-danger-bg flex items-center justify-center mb-4">
            <Lock size={26} className="text-danger" />
          </div>

          <h1 id={HEADING_ID} className="text-2xl font-bold text-brand-navy mb-2">
            You Have Been Removed
          </h1>

          <p className="text-gray-600 mb-6" aria-live="polite">
            Your session ended due to inactivity. Click below to access the form again.
          </p>

          <div className="flex flex-col gap-2">
            <button
              ref={requestButtonRef}
              type="button"
              onClick={reclaim}
              className="w-full py-2.5 px-4 rounded-md bg-brand-navy text-white font-semibold hover:bg-brand-navy-light disabled:opacity-40 disabled:cursor-not-allowed transition-colors focus:outline-none focus:ring-2 focus:ring-brand-green-50 cursor-pointer"
            >
              Access Form
            </button>
            <button
              type="button"
              onClick={() => { void logout(); }}
              className="w-full py-2.5 px-4 rounded-md border border-gray-300 bg-white text-gray-700 font-semibold hover:bg-gray-50 transition-colors flex items-center justify-center gap-2 focus:outline-none focus:ring-2 focus:ring-brand-green-50 cursor-pointer"
            >
              <LogOut size={16} />
              Sign Out
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={HEADING_ID}
      className="min-h-screen flex items-center justify-center bg-gray-50 px-6"
    >
      <div className="max-w-md w-full bg-white rounded-xl border border-gray-200 p-8 shadow-sm text-center">
        {!transitionComplete ? (
          <>
            <div className="w-14 h-14 mx-auto rounded-full bg-warning-bg flex items-center justify-center mb-4">
              <Loader2 size={26} className="text-warning animate-spin" />
            </div>

            <h1 id={HEADING_ID} className="text-2xl font-bold text-brand-navy mb-2">
              {isSameUserHolder ? "Editing Elsewhere" : "You Have Been Removed"}
            </h1>

            {/* aria-live="polite" announces the message to screen readers when
                this component mounts; the removal is sudden from the user's POV
                and they should hear what happened, not just see the layout shift. */}
            <p className="text-gray-600" aria-live="polite">
              {isSameUserHolder
                ? "This form is now open in another tab or device under your account. Just a moment..."
                : <><span className="font-semibold text-gray-800 break-words">{lockedBy ?? "Another user"}</span> is now taking over the form. Just a moment...</>}
            </p>
          </>
        ) : (
          <>
            <div className="w-14 h-14 mx-auto rounded-full bg-danger-bg flex items-center justify-center mb-4">
              <Lock size={26} className="text-danger" />
            </div>

            <h1 id={HEADING_ID} className="text-2xl font-bold text-brand-navy mb-2">
              {isSameUserHolder ? "Editing Elsewhere" : "You Have Been Removed"}
            </h1>

            <p className="text-gray-600 mb-6" aria-live="polite">
              {isSameUserHolder
                ? "This form is now open in another tab or device under your account."
                : <>You have been removed by <span className="font-semibold text-gray-800 break-words">{lockedBy ?? "another user"}</span>. They are now editing this form.</>}
            </p>

            {!isSameUserHolder && otherUserRequesting && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-md px-3 py-2 mb-4 flex items-start gap-2 text-left">
                <AlertCircle size={16} className="text-amber-700 shrink-0 mt-0.5" />
                <p className="text-xs text-amber-800">
                  <span className="font-semibold break-words">{requestInProgress}</span> is currently requesting access. The button will re-enable when their request completes.
                </p>
              </div>
            )}

            <div className="flex flex-col gap-2">
              <button
                ref={requestButtonRef}
                type="button"
                onClick={isSameUserHolder ? reclaim : requestAccess}
                disabled={buttonDisabled}
                className="w-full py-2.5 px-4 rounded-md bg-brand-navy text-white font-semibold hover:bg-brand-navy-light disabled:opacity-40 disabled:cursor-not-allowed transition-colors focus:outline-none focus:ring-2 focus:ring-brand-green-50 cursor-pointer"
              >
                {isSameUserHolder ? "Take Over Here" : "Request Access"}
              </button>
              <button
                type="button"
                onClick={() => { void logout(); }}
                className="w-full py-2.5 px-4 rounded-md border border-gray-300 bg-white text-gray-700 font-semibold hover:bg-gray-50 transition-colors flex items-center justify-center gap-2 focus:outline-none focus:ring-2 focus:ring-brand-green-50 cursor-pointer"
              >
                <LogOut size={16} />
                Sign Out
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
